import { useCallback } from 'react';
import { useCurrentAccount } from '../../auth/hooks/useCurrentAccount';
import { useUpdateAccount } from '../../auth/hooks/useUpdateAccount';
import { useTheme, type ThemePreference } from './useTheme';

/**
 * Sets the appearance preference and persists it to the account.
 *
 * This is what the Settings picker must call. `useTheme().setTheme` writes
 * local storage and the query cache only, which is right for the reconciliation
 * ProtectedRoute does on load but wrong for a user choice: the account is the
 * cross-device source of truth and `useSetDefaultTheme` lets it win on every
 * authenticated load. A local-only pick would therefore survive until the next
 * reload and then be overwritten by the stale account value, as a visible
 * post-mount flash - the exact thing the pre-paint bootstrap exists to prevent.
 *
 * The local write happens first and is not awaited on the mutation: the page is
 * its own preview, so the change has to land on the current frame. A failed
 * account write leaves the choice applied on this device and rejects, so the
 * caller can surface it.
 */
export const useUpdateTheme = () => {
  const { setTheme } = useTheme();
  const { account } = useCurrentAccount();
  const { mutateAsync } = useUpdateAccount();

  return useCallback(
    async (next: ThemePreference): Promise<void> => {
      setTheme(next);

      // Signed out (the anonymous flows reach nothing that renders the picker,
      // but the hook must not throw if one ever does), or the generated
      // `Account.id` is absent: keep the choice local. Sending
      // `{ id: undefined }` is a silent API no-op rather than an error.
      if (!account?.id) return;

      await mutateAsync({
        input: {
          id: account.id,
          preferences: {
            ...account.preferences,
            theme: next,
          },
        },
      });
    },
    [account, mutateAsync, setTheme],
  );
};
